import React, { useState } from 'react';
import { Edit2, Trash2, Eye, Briefcase, X, Download, Printer } from 'lucide-react';

// ── CSV export ───────────────────────────────────────────────────────────────
function exportPositionToCSV(position, departments, positions) {
  const department = departments.find(d => d.id === position.departamentoId);
  const reportsToPosition = (Array.isArray(positions) ? positions : []).find(p => p && p.id === position.reportaA);
  const reportingPositions = (Array.isArray(positions) ? positions : []).filter(p => p && p.reportaA === position.id);
  const relatedDepartments = departments.filter(d =>
    position.relacionDepartamentos?.includes(d.id)
  );

  let csv = '\ufeff';
  csv += 'DESCRIPCIÓN DE CARGO\n';
  csv += '7Family - Sistema de Gestión de Personal\n\n';
  csv += 'INFORMACIÓN BÁSICA\n';
  csv += `Nombre del Cargo:,${position.nombre}\n`;
  csv += `Departamento:,${department?.nombre || 'Sin departamento'}\n`;
  csv += `Reporta a:,${reportsToPosition ? reportsToPosition.nombre : 'Ninguno'}\n`;
  csv += `Le reportan:,${reportingPositions.length} cargo(s)\n`;
  if (reportingPositions.length > 0) {
    reportingPositions.forEach(pos => csv += `,${pos.nombre}\n`);
  }
  csv += '\n';

  if (position.proposito) {
    csv += 'PROPÓSITO\n';
    csv += `"${position.proposito.replace(/"/g, '""')}"\n\n`;
  }
  if (position.responsabilidades) {
    csv += 'RESPONSABILIDADES\n';
    csv += `"${position.responsabilidades.replace(/"/g, '""')}"\n\n`;
  }
  if (position.equipamiento) {
    csv += 'EQUIPAMIENTO\n';
    csv += `"${position.equipamiento.replace(/"/g, '""')}"\n\n`;
  }
  if (position.condicionesTrabajo) {
    csv += 'CONDICIONES DE TRABAJO\n';
    csv += `"${position.condicionesTrabajo.replace(/"/g, '""')}"\n\n`;
  }
  if (relatedDepartments.length > 0) {
    csv += 'RELACIÓN INTERDEPARTAMENTAL\n';
    relatedDepartments.forEach(dept => csv += `${dept.nombre}\n`);
    csv += '\n';
  }

  csv += 'PERFIL DEL CARGO\n';
  csv += `Nivel Educativo:,${position.nivelEducativo || 'No especificado'}\n`;
  csv += `Experiencia:,${position.experienciaRequerida || 'No especificada'}\n`;
  csv += `Conocimientos:,${position.conocimientosEspecificos || 'No especificados'}\n`;
  csv += `Habilidades Técnicas:,${position.habilidadesTecnicas || 'No especificadas'}\n`;
  csv += `Habilidades Blandas:,${position.habilidadesBlandas || 'No especificadas'}\n`;
  csv += `Certificaciones:,${position.certificacionesRequeridas || 'No especificadas'}\n`;
  csv += `Idiomas:,${position.idiomas || 'No especificados'}\n\n`;

  if (position.salarioRango || position.beneficios) {
    csv += 'COMPENSACIÓN\n';
    if (position.salarioRango) csv += `Rango Salarial:,${position.salarioRango}\n`;
    if (position.beneficios) csv += `Beneficios:,${position.beneficios}\n`;
    csv += '\n';
  }
  if (position.horario) {
    csv += 'HORARIO\n';
    csv += `${position.horario}\n\n`;
  }

  csv += `Generado:,${new Date().toLocaleString('es-DO')}\n`;

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `Cargo_${position.nombre.replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
  link.click();
}

// ── Print / PDF ──────────────────────────────────────────────────────────────
function printPosition(position, departments, positions) {
  const department = departments.find(d => d.id === position.departamentoId);
  const reportsToPosition = (Array.isArray(positions) ? positions : []).find(p => p && p.id === position.reportaA);
  const reportingPositions = (Array.isArray(positions) ? positions : []).filter(p => p && p.reportaA === position.id);

  const html = `
<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Descripción - ${position.nombre}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; padding: 40px; background: #f5f5f5; line-height: 1.6; }
    .container { max-width: 900px; margin: 0 auto; background: white; padding: 40px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
    .header { background: ${department?.color || '#2563eb'}; color: white; padding: 30px; margin: -40px -40px 30px -40px; }
    .header h1 { font-size: 28px; margin-bottom: 8px; }
    .section { margin-bottom: 25px; page-break-inside: avoid; }
    .section-title { background: #f0f0f0; padding: 12px 15px; font-weight: bold; margin-bottom: 15px; border-left: 4px solid ${department?.color || '#2563eb'}; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
    .info-item { padding: 12px; border: 2px solid #e0e0e0; border-radius: 8px; background: #fafafa; }
    .info-item label { font-weight: bold; color: #555; font-size: 12px; display: block; margin-bottom: 5px; }
    .text-block { padding: 15px; background: #f9f9f9; border-left: 3px solid ${department?.color || '#2563eb'}; white-space: pre-line; line-height: 1.8; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
    th, td { padding: 10px; text-align: left; border-bottom: 1px solid #e0e0e0; }
    th { background: #f0f0f0; font-weight: bold; }
    .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 2px solid #e0e0e0; font-size: 11px; color: #999; }
    @media print { body { background: white; padding: 0; } .container { box-shadow: none; } .no-print { display: none; } }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>DESCRIPCIÓN DE CARGO</h1>
      <p>7Family - Sistema de Gestión de Personal</p>
    </div>
    <div class="section">
      <div class="section-title">INFORMACIÓN BÁSICA</div>
      <div class="info-grid">
        <div class="info-item"><label>Cargo:</label>${position.nombre}</div>
        <div class="info-item"><label>Departamento:</label>${department?.nombre || 'N/A'}</div>
        <div class="info-item"><label>Reporta a:</label>${reportsToPosition?.nombre || 'Ninguno'}</div>
        <div class="info-item"><label>Le reportan:</label>${reportingPositions.length} cargo(s)</div>
      </div>
    </div>
    ${position.proposito ? `<div class="section"><div class="section-title">PROPÓSITO</div><div class="text-block">${position.proposito}</div></div>` : ''}
    ${position.responsabilidades ? `<div class="section"><div class="section-title">RESPONSABILIDADES</div><div class="text-block">${position.responsabilidades}</div></div>` : ''}
    <div class="section">
      <div class="section-title">PERFIL REQUERIDO</div>
      <table>
        <tr><th>Aspecto</th><th>Requisito</th></tr>
        <tr><td><strong>Nivel Educativo</strong></td><td>${position.nivelEducativo || 'No especificado'}</td></tr>
        <tr><td><strong>Experiencia</strong></td><td>${position.experienciaRequerida || 'No especificada'}</td></tr>
        ${position.conocimientosEspecificos ? `<tr><td><strong>Conocimientos</strong></td><td>${position.conocimientosEspecificos}</td></tr>` : ''}
        ${position.habilidadesTecnicas ? `<tr><td><strong>Habilidades Técnicas</strong></td><td>${position.habilidadesTecnicas}</td></tr>` : ''}
        ${position.habilidadesBlandas ? `<tr><td><strong>Habilidades Blandas</strong></td><td>${position.habilidadesBlandas}</td></tr>` : ''}
      </table>
    </div>
    <div class="footer">Generado: ${new Date().toLocaleString('es-DO')} | 7Family RRHH</div>
    <div class="no-print" style="text-align: center; margin-top: 30px;">
      <button onclick="window.print()" style="background: ${department?.color || '#2563eb'}; color: white; border: none; padding: 12px 30px; border-radius: 8px; font-weight: bold; cursor: pointer; margin-right: 10px;">Imprimir / PDF</button>
      <button onclick="window.close()" style="background: #6c757d; color: white; border: none; padding: 12px 30px; border-radius: 8px; font-weight: bold; cursor: pointer;">Cerrar</button>
    </div>
  </div>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win || win.closed || typeof win.closed === 'undefined') {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Cargo_${position.nombre.replace(/[^a-zA-Z0-9]/g, '_')}.html`;
    link.click();
    alert('Archivo descargado. Ábrelo y usa Ctrl+P para guardar como PDF.');
  } else {
    win.document.write(html);
    win.document.close();
  }
}

// ── Position Details Modal (also exported for use in OrgChart view) ──────────
export function PositionDetailsModal({ position, departments, positions, onClose }) {
  const department = departments.find(d => d.id === position.departamentoId);
  const reportsToPosition = (Array.isArray(positions) ? positions : []).find(p => p && p.id === position.reportaA);
  const reportingPositions = (Array.isArray(positions) ? positions : []).filter(p => p && p.reportaA === position.id);
  const deptColor = department?.color || '#2563eb';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-auto">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div
          className="p-6 flex items-center justify-between border-b-4"
          style={{ borderBottomColor: deptColor }}
        >
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl" style={{ backgroundColor: deptColor + '20' }}>
              <Briefcase className="w-6 h-6" style={{ color: deptColor }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{position.nombre}</h2>
              <p className="text-sm text-gray-600">{department?.nombre || 'Sin departamento'}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => exportPositionToCSV(position, departments, positions)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-semibold"
              title="Exportar a CSV/Excel"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">CSV</span>
            </button>
            <button
              onClick={() => printPosition(position, departments, positions)}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 text-sm font-semibold"
              title="Imprimir o guardar como PDF"
            >
              <Printer className="w-4 h-4" />
              <span className="hidden sm:inline">PDF</span>
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Jerarquía */}
          <div>
            <h3 className="text-lg font-bold text-gray-800 mb-3">Jerarquía</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-xs text-blue-700 font-semibold mb-1">Reporta a:</p>
                <p className="text-sm text-gray-800">
                  {reportsToPosition ? reportsToPosition.nombre : 'Ninguno (Nivel superior)'}
                </p>
              </div>
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                <p className="text-xs text-gray-600 font-semibold mb-1">Le reportan:</p>
                {reportingPositions.length > 0 ? (
                  <div className="space-y-1">
                    {reportingPositions.map(pos => (
                      <p key={pos.id} className="text-sm text-gray-800">• {pos.nombre}</p>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Ninguno</p>
                )}
              </div>
            </div>
          </div>

          {position.proposito && (
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Propósito del Cargo</h3>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{position.proposito}</p>
            </div>
          )}

          {position.responsabilidades && (
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Responsabilidades</h3>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{position.responsabilidades}</p>
              </div>
            </div>
          )}

          {/* Perfil */}
          {(position.nivelEducativo || position.experienciaRequerida || position.habilidadesTecnicas || position.habilidadesBlandas) && (
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-3">Perfil Requerido</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {position.nivelEducativo && (
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-xs text-gray-500 font-semibold mb-1">Nivel Educativo</p>
                    <p className="text-sm text-gray-800">{position.nivelEducativo}</p>
                  </div>
                )}
                {position.experienciaRequerida && (
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-xs text-gray-500 font-semibold mb-1">Experiencia</p>
                    <p className="text-sm text-gray-800">{position.experienciaRequerida}</p>
                  </div>
                )}
                {position.habilidadesTecnicas && (
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-xs text-gray-500 font-semibold mb-1">Habilidades Técnicas</p>
                    <p className="text-sm text-gray-800">{position.habilidadesTecnicas}</p>
                  </div>
                )}
                {position.habilidadesBlandas && (
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-xs text-gray-500 font-semibold mb-1">Habilidades Blandas</p>
                    <p className="text-sm text-gray-800">{position.habilidadesBlandas}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main list component ───────────────────────────────────────────────────────
export default function PositionList({ positions, departments, onEdit, onDelete }) {
  const [viewingPosition, setViewingPosition] = useState(null);

  const validPositions  = Array.isArray(positions)   ? positions   : [];
  const validDepartments = Array.isArray(departments) ? departments : [];

  const getDepartmentName  = (deptId) => validDepartments.find(d => d.id === deptId)?.nombre || '—';
  const getDepartmentColor = (deptId) => validDepartments.find(d => d.id === deptId)?.color  || '#6b7280';
  const getReportsToName   = (id)     => validPositions.find(p => p.id === id)?.nombre        || null;
  const getReportingCount  = (id)     => validPositions.filter(p => p && p.reportaA === id).length;

  if (validPositions.length === 0) {
    return (
      <div className="py-16 text-center">
        <Briefcase className="w-12 h-12 text-gray-200 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-500 mb-1">No hay cargos registrados</p>
        <p className="text-xs text-gray-400">Crea el primer cargo para comenzar</p>
      </div>
    );
  }

  return (
    <div>
      {viewingPosition && (
        <PositionDetailsModal
          position={viewingPosition}
          departments={validDepartments}
          positions={validPositions}
          onClose={() => setViewingPosition(null)}
        />
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-blue-600 text-white">
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Cargo</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Departamento</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Reporta A</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Educación</th>
              <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider">Reportes</th>
              <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {validPositions.map(position => {
              const deptColor      = getDepartmentColor(position.departamentoId);
              const deptName       = getDepartmentName(position.departamentoId);
              const reportsToName  = getReportsToName(position.reportaA);
              const reportingCount = getReportingCount(position.id);

              return (
                <tr key={position.id} className="hover:bg-gray-50 transition-colors">
                  {/* Cargo */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 flex-shrink-0" style={{ color: deptColor }} />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{position.nombre}</div>
                        {position.proposito && (
                          <div className="text-xs text-gray-500 truncate max-w-xs mt-0.5">{position.proposito}</div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Departamento */}
                  <td className="px-6 py-4">
                    <span
                      className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold text-white"
                      style={{ backgroundColor: deptColor }}
                    >
                      {deptName}
                    </span>
                  </td>

                  {/* Reporta A */}
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-700">{reportsToName || '—'}</span>
                  </td>

                  {/* Educación */}
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-700">{position.nivelEducativo || '—'}</span>
                  </td>

                  {/* Reportes directos */}
                  <td className="px-6 py-4 text-center">
                    {reportingCount > 0 ? (
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
                        {reportingCount}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </td>

                  {/* Acciones */}
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setViewingPosition(position)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Ver
                      </button>
                      <button
                        onClick={() => onEdit(position)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                        Editar
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`¿Eliminar el cargo "${position.nombre}"?`)) {
                            onDelete(position.id);
                          }
                        }}
                        className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-50 border-t border-gray-200">
            <tr>
              <td colSpan="6" className="px-6 py-3">
                <p className="text-xs text-gray-500">
                  <span className="font-semibold text-gray-700">{validPositions.length}</span>{' '}
                  cargo{validPositions.length !== 1 ? 's' : ''}
                </p>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
